import React, { useState } from 'react';
import { Check, ChevronRight, Loader2, SkipForward } from 'lucide-react';
import { DRIVER_FLOW, statusLabel } from '../lib/loadStatus';

// Mirrors the backend's forward-only driver transitions (routers/loads.py update_load_status).
const DRIVER_TRANSITIONS = {
  assigned: ['en_route_pickup', 'at_shipper', 'in_transit'],
  en_route_pickup: ['at_shipper', 'in_transit'],
  at_shipper: ['picked_up', 'in_transit'],
  picked_up: ['in_transit', 'at_receiver'],
  in_transit: ['at_receiver', 'delivered'],
  at_receiver: ['delivered'],
  recovered: ['en_route_pickup', 'at_shipper', 'in_transit'],
};

/**
 * Driver trip stepper: Assigned → En Route → Arrived at Pickup → Picked Up → In Transit → Arrived at Delivery → Delivered.
 * Delivered always goes through onComplete (Complete Trip / POD gate), never a bare status post.
 */
const LoadStatusStepper = ({ load, onAdvance, onComplete, busy = false }) => {
  const [skipOpen, setSkipOpen] = useState(false);
  const current = load?.status;
  const idx = DRIVER_FLOW.indexOf(current);
  const allowed = DRIVER_TRANSITIONS[current] || [];
  const next = DRIVER_FLOW[idx + 1];
  const nextAllowed = allowed.includes(next) ? next : allowed[0];
  const skips = allowed.filter((s) => s !== nextAllowed && s !== 'delivered');

  const go = (target, skipped = []) => {
    if (target === 'delivered') { onComplete?.(); return; }
    if (skipped.length) {
      const ok = window.confirm(`You are skipping: ${skipped.map(statusLabel).join(', ')}.\n\nMark this load “${statusLabel(target)}” now?`);
      if (!ok) return;
    }
    setSkipOpen(false);
    onAdvance?.(target);
  };

  const last = (load?.status_history || []).slice(-1)[0];

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="load-status-stepper" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Trip progress</p>
        <span className="text-[11px] text-slate-500" data-testid="stepper-current-label">Now: <b className="text-slate-800">{statusLabel(current)}</b></span>
      </div>
      <ol className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="stepper-steps">
        {DRIVER_FLOW.map((s, i) => {
          const done = i < idx || current === 'delivered';
          const active = s === current;
          return (
            <li key={s} className="flex items-center gap-1 flex-shrink-0" data-testid={`stepper-step-${s}`} data-state={done ? 'done' : active ? 'active' : 'todo'}>
              <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border ${done ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : active ? 'bg-[#002FA7] border-[#002FA7] text-white' : 'bg-white border-slate-200 text-slate-400'}`}>
                {done ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 inline-flex items-center justify-center text-[9px]">{i + 1}</span>}
                {statusLabel(s)}
              </div>
              {i < DRIVER_FLOW.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
            </li>
          );
        })}
      </ol>
      {last && (
        <p className="text-[11px] text-slate-500 mt-2" data-testid="stepper-last-change">
          Last update: {statusLabel(last.to_status)} · {last.user_name || 'you'} · {last.at ? new Date(last.at).toLocaleString() : ''}
        </p>
      )}
      {nextAllowed && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            data-testid="stepper-next-button"
            disabled={busy}
            onClick={() => go(nextAllowed)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-bold text-white disabled:opacity-50 active:scale-95 transition-transform ${nextAllowed === 'delivered' ? 'bg-[#002FA7] hover:bg-[#00247D]' : 'bg-[#34C759] hover:bg-green-700'}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
            {nextAllowed === 'delivered' ? 'Complete Trip (POD required)' : `Mark “${statusLabel(nextAllowed)}”`}
          </button>
          {skips.length > 0 && (
            <div className="relative">
              <button type="button" data-testid="stepper-skip-toggle" onClick={() => setSkipOpen((v) => !v)} className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50">
                <SkipForward className="w-3.5 h-3.5" /> Skip ahead
              </button>
              {skipOpen && (
                <div className="absolute z-20 mt-1 bg-white border border-slate-200 rounded-md shadow-lg min-w-[220px]" data-testid="stepper-skip-menu">
                  {skips.map((s) => {
                    const skipped = DRIVER_FLOW.slice(idx + 1, DRIVER_FLOW.indexOf(s));
                    return (
                      <button key={s} type="button" data-testid={`stepper-skip-${s}`} onClick={() => go(s, skipped)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50">
                        <span className="font-semibold text-slate-800">{statusLabel(s)}</span>
                        <span className="block text-[10px] text-amber-700">skips {skipped.map(statusLabel).join(', ')} · asks to confirm</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-2">Canceled, Rejected and Recovered are set by dispatch. Every change records who, when and the previous status.</p>
    </div>
  );
};

export default LoadStatusStepper;
